import React, { useEffect, useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { templateStudioStateService } from 'state/template-studio.state.service';
import { z } from 'zod';

import { BlockMetadata } from 'components/TemplatesStudio/DesignsMetadata/types';
import { EThemes } from 'components/TemplatesStudio/enums';

import { templateStudioSchema } from '../../../validation-schema';
import { DesignSidebarItem } from '../Components/DesignSidebarItem';

const REWARD_CALENDAR_OBJECT_PATH = 'rewardCalendar';

const getRewardCalendarBadgeDesignsByFilter = ({
  themes
}: {
  themes: EThemes[];
}): BlockMetadata[] => {
  const relevantDesigns =
    templateStudioStateService.relevantRewardCalendarBadgeDesigns.get() || [];

  return relevantDesigns.filter((currentDesign) => {
    return (
      !themes || themes.length === 0 || themes.includes(currentDesign.theme)
    );
  });
};

const getRewardCalendarDesignsByFilter = ({
  themes
}: {
  themes: EThemes[];
}): BlockMetadata[] => {
  const relevantDesigns =
    templateStudioStateService.relevantRewardCalendarDesigns.get() || [];

  return relevantDesigns.filter((currentDesign) => {
    return (
      !themes || themes.length === 0 || themes.includes(currentDesign.theme)
    );
  });
};

export const RewardCalendarBadgeSecondarySidebar: React.FC = () => {
  const { control } = useFormContext<z.infer<typeof templateStudioSchema>>();

  const rewardCalendarObject = useWatch({
    control,
    name: REWARD_CALENDAR_OBJECT_PATH
  });

  const themes = rewardCalendarObject?.themes ?? [];

  const designs = useMemo(() => {
    return getRewardCalendarBadgeDesignsByFilter({ themes });
  }, [themes]);

  return (
    <React.Fragment>
      {designs.map((design) => (
        <DesignSidebarItem
          key={design.id}
          {...design}
          blockDesignIdPath="rewardCalendar.selectedBadgeDesignId"
        />
      ))}
    </React.Fragment>
  );
};

export const RewardCalendarDesignsSecondarySidebar: React.FC = () => {
  const { setDisplayPopupPreview } = useTemplateStudioState();
  const { control } = useFormContext<z.infer<typeof templateStudioSchema>>();

  const rewardCalendarObject = useWatch({
    control,
    name: REWARD_CALENDAR_OBJECT_PATH
  });

  const themes = rewardCalendarObject?.themes ?? [];

  const designs = useMemo(() => {
    return getRewardCalendarDesignsByFilter({ themes });
  }, [themes]);

  useEffect(() => {
    setDisplayPopupPreview(true);
    return () => {
      setDisplayPopupPreview(false);
    };
  }, [setDisplayPopupPreview]);

  return (
    <React.Fragment>
      {designs.map((design) => (
        <DesignSidebarItem
          key={design.id}
          {...design}
          blockDesignIdPath="rewardCalendar.selectedDesignId"
        />
      ))}
    </React.Fragment>
  );
};
